import React, { useState, useEffect, memo, useCallback } from 'react';
import { auth } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import { getInvoiceGroupByEmail, getInvoiceGroupByProjectId } from '../helpers/invoiceGroup';
import { compareProjectIds, fetchQuotesForUser } from '../helpers/quoteVisibility';
import { getProductSpecsText } from '../helpers/quotePdfImages';
import { formatQuoteDate, maskVendorLabel } from '../helpers/quoteDisplayUtils';
import { useQuoteUserRole } from '../helpers/useQuoteUserRole';

// Separate FilterInput component
const FilterInput = memo(({ field, placeholder, value, onChange }) => (
  <input
    type="text"
    placeholder={`Filter ${placeholder}...`}
    value={value}
    onChange={(e) => onChange(field, e.target.value)}
    className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
));

const StepTwo = () => {
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const { userRole, currentUserUid, isVendor, roleFetched } = useQuoteUserRole();
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortField, setSortField] = useState('createdOn');
  const [filters, setFilters] = useState({
    projectId: '',
    companyName: '',
    projectName: '',
    salesRepName: '',
    productTypes: '',
    createdOn: ''
  });
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [invoiceGroupFilter, setInvoiceGroupFilter] = useState('ALL');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!roleFetched) return;
    const queryInvoiceGroup = new URLSearchParams(location.search).get('group');
    if (queryInvoiceGroup === 'LL' || queryInvoiceGroup === 'ET' || queryInvoiceGroup === 'ALL') {
      setInvoiceGroupFilter(queryInvoiceGroup);
      return;
    }

    if (userRole === 'PackagingAdmin' || userRole === 'ShippingAdmin') {
      setInvoiceGroupFilter('ALL');
      return;
    }

    setInvoiceGroupFilter(getInvoiceGroupByEmail(auth.currentUser?.email));
  }, [location.search, roleFetched, userRole]);

  useEffect(() => {
    const fetchQuotes = async () => {
      if (!currentUserUid || !roleFetched) return;

      setLoading(true);
      try {
        const quotesData = await fetchQuotesForUser({ currentUserUid, userRole });
        setQuotes(quotesData);
      } catch (error) {
        console.error('Error fetching quotes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, [userRole, currentUserUid, roleFetched]);

  const getQuoteInvoiceGroup = useCallback((quote) => {
    return quote.invoiceGroup || getInvoiceGroupByProjectId(quote.projectId);
  }, []);

  const formatProductTypes = useCallback((quote) => {
    const items = quote.products || [];
    if (!items.length) return '—';
    return items.map((product, index) => {
      const duplicates = items.filter((p) => p.productType === product.productType).length > 1;
      if (!duplicates) return product.productType;
      const spec = getProductSpecsText(product);
      const short = spec && spec !== product.productType
        ? (spec.length > 24 ? `${spec.slice(0, 24)}…` : spec)
        : String(index + 1);
      return `${product.productType} (${short})`;
    }).join(', ');
  }, []);

  useEffect(() => {
    const filtered = quotes.filter((quote) => {
      const quoteInvoiceGroup = getQuoteInvoiceGroup(quote);
      const matchesInvoiceGroup = invoiceGroupFilter === 'ALL' || quoteInvoiceGroup === invoiceGroupFilter;
      if (!matchesInvoiceGroup) return false;

      return Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true;

        let quoteValue = quote[key];
        if (key === 'productTypes') {
          quoteValue = formatProductTypes(quote);
        } else if (key === 'createdOn') {
          quoteValue = formatQuoteDate(quote[key]);
          if (quoteValue === '—') return false;
        } else if (key === 'companyName') {
          const customerName = quote.customerName || '';
          const companyName = quote.companyName || '';
          return customerName.toLowerCase().includes(filterValue.toLowerCase()) ||
                 companyName.toLowerCase().includes(filterValue.toLowerCase());
        }

        return String(quoteValue).toLowerCase().includes(filterValue.toLowerCase());
      });
    });

    const sortedQuotes = [...filtered].sort((a, b) => {
      if (sortField === 'projectId') {
        const cmp = compareProjectIds(a.projectId, b.projectId);
        return sortOrder === 'asc' ? cmp : -cmp;
      }

      let valueA = a[sortField];
      let valueB = b[sortField];

      if (sortField === 'createdOn') {
        valueA = valueA?.toDate?.() || new Date(0);
        valueB = valueB?.toDate?.() || new Date(0);
      } else if (sortField === 'productTypes') {
        valueA = a.products?.map((p) => p.productType).join(', ');
        valueB = b.products?.map((p) => p.productType).join(', ');
      } else if (sortField === 'invoiceGroup') {
        valueA = getQuoteInvoiceGroup(a);
        valueB = getQuoteInvoiceGroup(b);
      }

      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      }
      return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
    });

    setFilteredQuotes(sortedQuotes);
  }, [quotes, filters, sortField, sortOrder, invoiceGroupFilter, getQuoteInvoiceGroup, formatProductTypes]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditClick = (quote) => {
    navigate(`/packaging-details/${quote.id}`);
  };

  const handleSort = (field) => {
    const newSortOrder = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
    setSortField(field);

  };

  return (
    <>
      <Header />
      <div className='parent-container pt-5 h-100 pb-10 p-4 sm:ml-64'>
        <h2 className='text-3xl p-10 mb-6' style={{ textAlign: 'center' }}>Update Quote</h2>
        <div className='width-full flex flex-col items-center'>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* VIP Clients Section */}
              {quotes.some(q => q.isVIPClient) && (
                <div className="w-full max-w-[900px] mb-6 rounded-xl overflow-hidden shadow-md border border-yellow-300">
                  <div className="bg-yellow-400 px-4 py-3 flex items-center justify-between">
                    <span className="font-bold text-yellow-900 text-base">⭐ VIP Clients</span>
                    <span className="text-yellow-800 text-sm font-medium">
                      {quotes.filter(q => q.isVIPClient).length} VIP client{quotes.filter(q => q.isVIPClient).length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="bg-yellow-50 max-h-[200px] overflow-y-auto divide-y divide-yellow-200">
                    {quotes.filter(q => q.isVIPClient).map((quote) => (
                      <div
                        key={quote.id}
                        className="px-4 py-3 cursor-pointer hover:bg-yellow-100 flex justify-between items-center transition-colors"
                        onClick={() => navigate(`/packaging-details/${quote.id}`)}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-800">{quote.projectId}</span>
                          <span className="text-gray-400">·</span>
                          <span className="text-gray-700">
                            {isVendor
                              ? maskVendorLabel(quote.customerName || quote.companyName)
                              : (quote.customerName || quote.companyName || '—')}
                          </span>
                          <span className="text-gray-400">·</span>
                          <span className="text-gray-600 text-sm">{quote.salesRepName}</span>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                          {quote.createdOn?.toDate?.()?.toLocaleDateString() ?? '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="w-full max-w-[900px] mb-4 p-4 bg-white rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-3">Packaging Details Views</h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setInvoiceGroupFilter('ALL')}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${invoiceGroupFilter === 'ALL' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setInvoiceGroupFilter('LL')}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${invoiceGroupFilter === 'LL' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Packaging Details - LL
                  </button>
                  <button
                    type="button"
                    onClick={() => setInvoiceGroupFilter('ET')}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${invoiceGroupFilter === 'ET' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Packaging Details - ET
                  </button>
                </div>
              </div>

              {/* Filter Section */}
              <div className="w-full max-w-[900px] mb-4 p-4 bg-white rounded-lg shadow">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                >
                  <h3 className="text-lg font-semibold">Filters</h3>
                  <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className={`h-5 w-5 transform transition-transform ${isFilterExpanded ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                {isFilterExpanded && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4">
                    <FilterInput 
                      field="projectId" 
                      placeholder="Project ID" 
                      value={filters.projectId}
                      onChange={handleFilterChange}
                    />
                    <FilterInput 
                      field="companyName" 
                      placeholder="Company Name" 
                      value={filters.companyName}
                      onChange={handleFilterChange}
                    />
                    <FilterInput 
                      field="projectName" 
                      placeholder="Project Name" 
                      value={filters.projectName}
                      onChange={handleFilterChange}
                    />
                    <FilterInput 
                      field="salesRepName" 
                      placeholder="Sales Rep" 
                      value={filters.salesRepName}
                      onChange={handleFilterChange}
                    />
                    <FilterInput 
                      field="productTypes" 
                      placeholder="Product Types" 
                      value={filters.productTypes}
                      onChange={handleFilterChange}
                    />
                    <FilterInput 
                      field="createdOn" 
                      placeholder="Created On" 
                      value={filters.createdOn}
                      onChange={handleFilterChange}
                    />
                  </div>
                )}
              </div>

              <p className="w-full max-w-[900px] mb-2 text-sm text-gray-600">
                Showing {filteredQuotes.length} of {quotes.length} project{quotes.length !== 1 ? 's' : ''}
                {filteredQuotes.length !== quotes.length ? ' (filtered)' : ''}
              </p>

              {/* Table Section */}
              <table style={{ maxWidth: '900px' }} className='w-full my-0 align-middle text-dark'>
                <thead className='align-bottom bg-gray-100'>
                  <tr className='font-semibold text-[0.95rem] text-secondary-dark'>
                    <th 
                      className='pb-3 text-start min-w-[100px] border-b-2 border-black cursor-pointer hover:text-blue-600'
                      onClick={() => handleSort('projectId')}
                    >
                      Project ID {sortField === 'projectId' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className='pb-3 text-start min-w-[120px] border-b-2 border-black cursor-pointer hover:text-blue-600'
                      onClick={() => handleSort('invoiceGroup')}
                    >
                      Invoice Group {sortField === 'invoiceGroup' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className='pb-3 text-start min-w-[175px] border-b-2 border-black cursor-pointer hover:text-blue-600'
                      onClick={() => handleSort('companyName')}
                    >
                      Company Name {sortField === 'companyName' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className='pb-3 text-start min-w-[175px] border-b-2 border-black cursor-pointer hover:text-blue-600'
                      onClick={() => handleSort('projectName')}
                    >
                      Project Name {sortField === 'projectName' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className='pb-3 text-end min-w-[100px] border-b-2 border-black cursor-pointer hover:text-blue-600'
                      onClick={() => handleSort('salesRepName')}
                    >
                      Sales Rep Name {sortField === 'salesRepName' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className='pb-3 text-end min-w-[100px] border-b-2 border-black cursor-pointer hover:text-blue-600'
                      onClick={() => handleSort('productTypes')}
                    >
                      Product Types {sortField === 'productTypes' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className='pb-3 pr-12 text-end min-w-[175px] border-b-2 border-black cursor-pointer hover:text-blue-600'
                      onClick={() => handleSort('createdOn')}
                    >
                      Created On {sortField === 'createdOn' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className='pb-3 text-end min-w-[50px] border-b-2 border-black'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote) => (
                    <tr 
                      key={quote.id} 
                      className='border-b border-gray-200 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors duration-150'
                      onClick={() => handleEditClick(quote)}
                    >
                      <td className='p-3 pl-0 overflow-x-auto max-w-[100px]'>
                        {quote.projectId}
                      </td>
                      <td className='p-3 pl-0 overflow-x-auto max-w-[120px]'>
                        {getQuoteInvoiceGroup(quote)}
                      </td>
                      <td className='p-3 pl-0 overflow-x-auto max-w-[175px]'>
                        {isVendor
                          ? maskVendorLabel(quote.customerName || quote.companyName)
                          : (quote.customerName || quote.companyName || '—')}
                      </td>
                      <td className='p-3 pl-0 overflow-x-auto max-w-[175px]'>
                        {isVendor ? maskVendorLabel(quote.projectName) : (quote.projectName || '—')}
                      </td>
                      <td className='p-3 pr-0 text-end overflow-x-auto max-w-[100px]'>{quote.salesRepName}</td>
                      <td className='p-3 pr-0 text-end overflow-x-auto max-w-[100px]'>
                        {formatProductTypes(quote)}
                      </td>
                      <td className='p-3 pr-12 text-end overflow-x-auto max-w-[175px]'>{formatQuoteDate(quote.createdOn)}</td>
                      <td className='p-3 pr-0 text-end overflow-x-auto max-w-[50px]'>
                        <span className='flex items-center justify-center p-0 m-0 leading-none shrink-0 text-secondary-dark'>
                          {userRole === 'PackagingAdmin' ? 'Edit' : 'View'}
                          <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth='1.5' stroke='currentColor' className='w-4 h-4'>
                            <path strokeLinecap='round' strokeLinejoin='round' d='M8.25 4.5l7.5 7.5-7.5 7.5' />
                          </svg>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StepTwo;
